import { useEffect, useState } from "react";
import { Trash2, X } from "lucide-react";
import type { DraftSummary, TimelineDraftService } from "@/lib/timeline-drafts";

interface DraftsListSheetProps {
  service: TimelineDraftService;
  userId: string;
  onClose: () => void;
  onRestore: (contextKey: string) => void;
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0s";
  const totalSeconds = Math.round(seconds);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return mins > 0 ? `${mins}m ${secs.toString().padStart(2, "0")}s` : `${secs}s`;
}

function formatRelativeTime(timestamp: number, now: number): string {
  const diff = Math.max(0, now - timestamp);
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

export default function DraftsListSheet({
  service,
  userId,
  onClose,
  onRestore,
}: DraftsListSheetProps) {
  const [drafts, setDrafts] = useState<DraftSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The drafts list is loaded eagerly on mount. Re-loaded after a delete so the
  // sheet reflects the live state without forcing a full modal remount.
  const reload = async () => {
    try {
      const all = await service.listDrafts(userId);
      setDrafts(all);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load drafts");
    }
  };

  useEffect(() => {
    void reload();
    // service / userId are stable for the modal lifetime; the effect runs once on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (contextKey: string) => {
    if (!window.confirm("Delete this draft? This can't be undone.")) return;
    try {
      await service.discardDraft(contextKey);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete draft");
    }
  };

  const now = Date.now();

  return (
    <div className="drafts-sheet-backdrop" onClick={onClose}>
      <div
        className="drafts-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drafts-sheet-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="drafts-sheet-header">
          <span className="drafts-sheet-title" id="drafts-sheet-title">
            Saved drafts
          </span>
          <button
            type="button"
            className="drafts-sheet-close"
            onClick={onClose}
            aria-label="Close drafts list"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="drafts-sheet-body">
        {error && <div className="drafts-sheet-error">{error}</div>}

        {drafts === null && !error && (
          <div className="drafts-sheet-empty">Loading…</div>
        )}

        {drafts !== null && drafts.length === 0 && (
          <div className="drafts-sheet-empty">
            No drafts yet. The timeline editor autosaves your work — anything you
            edit will show up here.
          </div>
        )}

        {drafts !== null && drafts.length > 0 && (
          <ul className="drafts-sheet-list" role="list">
            {drafts.map((draft) => (
              <li key={draft.contextKey} className="drafts-sheet-item">
                <button
                  type="button"
                  className="drafts-sheet-item-main"
                  onClick={() => onRestore(draft.contextKey)}
                  aria-label={`Resume draft from ${formatRelativeTime(draft.updatedAt, now)}`}
                >
                  <span className="drafts-sheet-item-primary">
                    {draft.projectName || "Untitled draft"}
                  </span>
                  <span className="drafts-sheet-item-secondary">
                    {draft.aspectRatio} · {draft.clipCount} clip{draft.clipCount === 1 ? "" : "s"} ·{" "}
                    {formatDuration(draft.totalDuration)} · saved {formatRelativeTime(draft.updatedAt, now)}
                  </span>
                </button>
                <button
                  type="button"
                  className="drafts-sheet-item-delete"
                  onClick={() => handleDelete(draft.contextKey)}
                  aria-label="Delete draft"
                  title="Delete draft"
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        </div>
      </div>
    </div>
  );
}
